import { marked } from 'marked';
import htmlToPdfmake from 'html-to-pdfmake';
import { format } from 'date-fns';

/**
 * Converts markdown to PDF-compatible format
 * @param markdown Markdown content
 * @returns PDF-compatible content
 */
export const markdownToPdf = (markdown: string) => {
  if (!markdown) return [];
  
  try {
    // Convert markdown to HTML
    const html = marked.parse(markdown) as string;
    
    // Convert HTML to pdfmake compatible format
    return htmlToPdfmake(html);
  } catch (error) {
    console.error('Error converting markdown to PDF:', error);
    return [{ text: 'Error rendering content', style: 'error' }];
  }
};

/**
 * Formats the current date for display in the PDF
 * @returns Formatted date string (e.g., "March 17, 2025")
 */
export const getFormattedDate = () => {
  return format(new Date(), 'MMMM dd, yyyy');
};

/**
 * Creates a footer for PDF pages
 * @param currentPage Current page number
 * @param totalPages Total number of pages
 * @param colors Color palette for styling
 * @returns Footer content object for pdfmake
 */
export const createFooter = (currentPage: number, totalPages: number, colors: Record<string, string>) => {
  return {
    stack: [
      {
        columns: [
          {
            text: 'www.metarouter.io - Confidential - © 2025 Nessa Labs, Inc. All rights reserved.',
            fontSize: 7,
            color: colors.mediumGrey,
            width: '*'
          },
          {
            columns: [
              {
                canvas: [
                  {
                    type: 'line',
                    x1: 0,
                    y1: 0,
                    x2: 21,
                    y2: 0,
                    lineWidth: 1,
                    lineColor: colors.primary
                  }
                ],
                width: 'auto',
                margin: [0, 3, 24, 0]
              },
              {
                text: `Page ${currentPage} of ${totalPages}`,
                fontSize: 7,
                color: colors.mediumGrey,
                width: 'auto',
                alignment: 'right'
              }
            ],
            width: 'auto'
          }
        ],
        margin: [32, 0, 32, 0]
      }
    ],
    margin: [0, 32, 0, 24],
    // Fix footer to bottom of page with 24px margin
    absolutePosition: { x: 0, y: 808 }
  };
};

/**
 * Creates a standard eyebrow section for PDF pages
 * @param title Title text for the eyebrow
 * @param markUrl URL for the mark image
 * @param colors Color palette for styling
 * @returns Eyebrow content objects for pdfmake
 */
export const createEyebrow = (title: string, markUrl: string | undefined, colors: Record<string, string>) => {
  return [
    // Eyebrow section
    {
      canvas: [
        {
          type: 'rect',
          x: 0,
          y: 0,
          w: 595,
          h: 48,
          color: colors.bgGrey
        }
      ],
      margin: [0, 0, 0, 0]
    },
    
    // Eyebrow content
    {
      columns: [
        {
          text: title,
          fontSize: 8,
          color: colors.black,
          margin: [32, -28, 0, 0]
        },
        {
          image: markUrl ? markUrl : '',
          width: 11,
          height: 10,
          alignment: 'right',
          margin: [0, -28, 16, 0]
        }
      ]
    }
  ];
};
